import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import BackdropModal from 'components/portal/backdrop-modal'
import { withI18next } from 'lib/i18n/withI18next'
import compose from 'recompose/compose'
import { hasLocizeTranslation } from 'lib/utils/locize'
import { BUTTON_HTML_TYPE, BUTTON_TYPES } from 'constants/button'
import Icon, { ICONS, ICON_SIZE } from 'components/common/icon'
import Typo from 'constants/typography'
import Button from 'components/common/button'
import { copyToClipboard } from 'lib/utils/common/commonUtils'
import Link from 'next/link'
import styles from './product-voucher-modal.scss'

const ProductVoucherModal = ({
  t,
  isPhone,
  renderTrigger,
  productVoucher,
  isShowPriceBreakDown,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)

  const openModal = useCallback(() => {
    setIsModalOpen(true)
  }, [])

  const closeModal = useCallback(() => {
    setIsModalOpen(false)
  }, [])

  return (
    <React.Fragment>
      <style jsx>{styles}</style>
      {renderTrigger({ openModal })}
      <BackdropModal
        onBackdropClick={closeModal}
        freezeBody={isPhone}
        isOpen={isModalOpen}
      >
        <div className={clsx('product-voucher-modal')}>
          {!isPhone && (
            <button
              className="product-voucher-modal__close-ctn"
              type={BUTTON_HTML_TYPE.button}
              onClick={() => {
                closeModal()
              }}
            >
              <Icon
                alt="close dialog"
                src={ICONS.close}
                size={ICON_SIZE.smallMedium}
              />
            </button>
          )}
          {isShowPriceBreakDown && productVoucher?.voucher && (
            <React.Fragment>
              <div className="product-voucher-modal__title">
                <span className={Typo.subtitle2}>
                  {hasLocizeTranslation(
                    t,
                    'PRICE_BREAKDOWN',
                    'Price Breakdown',
                  )}
                </span>
              </div>
              <div className="product-voucher-modal__title">
                <span className={Typo.body2}>
                  {hasLocizeTranslation(t, 'CURRENT_PRICE', 'Current Price')}
                </span>
                <span className={Typo.caption}>
                  {productVoucher?.prices?.original_price_formatted}
                </span>
              </div>
              <div className="product-voucher-modal__voucher-discount">
                <div>
                  <span className={Typo.body2}>
                    {hasLocizeTranslation(
                      t,
                      'VOUCHER_DISCOUNT',
                      'Voucher Discount',
                    )}
                  </span>
                  <div className="product-voucher-modal__voucher-discount__code">
                    <span className={Typo.body2}>
                      {productVoucher?.voucher?.code}
                    </span>
                    <div>
                      <Icon
                        lazyThreshold={0}
                        src={ICONS.copyPurple}
                        size={ICON_SIZE.small}
                      />
                      <Button
                        type={BUTTON_TYPES.link}
                        onClick={() =>
                          copyToClipboard(productVoucher?.voucher?.code)
                        }
                        className="product-voucher-modal__voucher-discount__code__copy-code"
                      >
                        <span className={Typo.caption}>
                          {hasLocizeTranslation(t, 'Copy Code', 'Copy Code')}
                        </span>
                      </Button>
                    </div>
                  </div>
                </div>
                <span
                  className={clsx(
                    'product-voucher-modal__voucher-discount__discount',
                    Typo.caption,
                  )}
                >
                  {productVoucher?.voucher?.voucher_discount_formatted}
                </span>
              </div>
              <div className="product-voucher-modal__estimated-price">
                <div>
                  <span className={Typo.subtitle2}>
                    {hasLocizeTranslation(
                      t,
                      'ESTIMATED_FINAL_PRICE',
                      'Estimated Final Price*',
                    )}
                  </span>
                  <div className="product-voucher-modal__estimated-price__notice">
                    <span className={Typo.caption}>
                      {hasLocizeTranslation(
                        t,
                        'ESTIMATED_FINAL_PRICE_NOTICE',
                        'Price may change according to your cart value',
                      )}
                    </span>
                  </div>
                </div>
                <span
                  className={clsx(
                    'product-voucher-modal__estimated-price__price',
                    Typo.subtitle2,
                  )}
                >
                  {productVoucher?.voucher?.estimated_final_price_formatted}
                </span>
              </div>
            </React.Fragment>
          )}
          <div className="product-voucher-modal__bpg-section">
            <div className="product-voucher-modal__bpg-section__info">
              <span className={Typo.body2}>
                {hasLocizeTranslation(
                  t,
                  'BEST_PRICE_GUARANTEED',
                  'Best Price Guaranteed',
                )}
              </span>
              <span className={Typo.caption}>
                {hasLocizeTranslation(
                  t,
                  'BEST_PRICE_GUARANTEE_INFO_1',
                  'Found a lower price elsewhere?',
                )}
              </span>
              <span className={Typo.caption}>
                {hasLocizeTranslation(
                  t,
                  'BEST_PRICE_GUARANTEE_INFO_2',
                  `Contact support & we'll credit the difference.`,
                )}
              </span>
            </div>
            <Link href="/info/privilege">
              <Button link type={BUTTON_TYPES.text}>
                <Icon src={ICONS.question} size={ICON_SIZE.medium} />
              </Button>
            </Link>
          </div>
        </div>
      </BackdropModal>
    </React.Fragment>
  )
}

ProductVoucherModal.defaultProps = {
  productVoucher: null,
}

ProductVoucherModal.propTypes = {
  t: PropTypes.func.isRequired,
  isPhone: PropTypes.bool.isRequired,
  renderTrigger: PropTypes.func.isRequired,
  isShowPriceBreakDown: PropTypes.bool.isRequired,
  productVoucher: PropTypes.shape({
    is_best_price_guaranteed: PropTypes.bool,
    prices: PropTypes.shape({
      original_price_formatted: PropTypes.string,
    }),
    voucher: PropTypes.shape({
      code: PropTypes.string,
      discount_text: PropTypes.string,
      discount_percentage_formatted: PropTypes.string,
      estimated_final_price_formatted: PropTypes.string,
      voucher_discount_formatted: PropTypes.string,
    }),
  }),
}

export default compose(withI18next())(ProductVoucherModal)
